<template>
  <section class="section donation-section reveal-section" id="donar" ref="sectionRef">
    <div class="section-header">
      <span class="section-label">Siembra Futuro</span>
      <h2 class="section-title">Tu Aporte Transforma</h2>
      <p class="section-subtitle">Cada donación se traduce en materiales educativos, capacitaciones y alcance misionero</p>
    </div>
    <div class="donation-box">
      <h2>Tu Aporte Siembra Futuro</h2>
      <p>Con tu generosidad, podemos continuar equipando a la próxima generación de líderes cristianos. Cada quetzal invertido es una semilla que produce fruto eterno en las vidas de niños y jóvenes guatemaltecos.</p>

      <div class="amount-grid">
        <button
          v-for="amount in amounts"
          :key="amount.value"
          class="amount-btn"
          :class="{ active: selectedAmount === amount.value && !customAmount }"
          @click="selectAmount(amount.value)"
        >
          Q{{ amount.label }}
        </button>
      </div>

      <div class="custom-input">
        <label for="customAmount">Monto personalizado (Q):</label>
        <input type="number" id="customAmount" v-model="customAmount" placeholder="Ingresa el monto" min="1" @input="selectedAmount = 0">
      </div>

      <div style="margin-bottom:2rem;">
        <p style="color:var(--vh-azul-profundo); font-weight:700; margin-bottom:1rem;">Método de pago:</p>
        <div class="method-grid">
          <div
            v-for="method in methods"
            :key="method.id"
            class="method-card"
            :class="{ active: selectedMethod === method.id }"
            @click="selectedMethod = method.id"
          >
            <i :class="method.icon"></i>
            <span>{{ method.label }}</span>
          </div>
        </div>
      </div>

      <ion-button expand="block" class="donate-action" :disabled="processing" @click="processDonation">
        <i v-if="!processing" class="fas fa-heart" style="margin-right:8px"></i>
        <i v-else class="fas fa-spinner fa-spin" style="margin-right:8px"></i>
        {{ processing ? 'Procesando...' : 'Realizar Donación' }}
      </ion-button>

      <div class="donation-confirm" :class="{ show: showConfirm }">
        <i class="fas fa-check-circle"></i> ¡Gracias por tu generosidad! Tu donación será procesada en breve.
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { IonButton } from '@ionic/vue'
import { useScrollReveal, useToast } from '@/composables/useAnimations'

const sectionRef = ref<HTMLElement | null>(null)
const { registerReveal } = useScrollReveal()
const { showToast } = useToast()
onMounted(() => registerReveal(sectionRef.value))

const amounts = ref([
  { value: 100, label: '100' },
  { value: 250, label: '250' },
  { value: 500, label: '500' },
  { value: 1000, label: '1,000' }
])
const methods = ref([
  { id: 'card', label: 'Tarjeta', icon: 'fas fa-credit-card' },
  { id: 'transfer', label: 'Transferencia', icon: 'fas fa-university' },
  { id: 'wallet', label: 'Billetera Digital', icon: 'fas fa-wallet' }
])

const selectedAmount = ref(250)
const customAmount = ref('')
const selectedMethod = ref('card')
const processing = ref(false)
const showConfirm = ref(false)

const selectAmount = (val: number) => {
  selectedAmount.value = val
  customAmount.value = ''
}

const processDonation = () => {
  const final = customAmount.value ? parseInt(customAmount.value) : selectedAmount.value
  if (final <= 0) { showToast('Por favor selecciona un monto válido', 'error'); return }
  processing.value = true
  setTimeout(() => {
    showConfirm.value = true
    processing.value = false
    showToast(`¡Donación de Q${final} registrada!`, 'success')
    selectedAmount.value = 250
    customAmount.value = ''
    selectedMethod.value = 'card'
    setTimeout(() => showConfirm.value = false, 5000)
  }, 2000)
}
</script>

<style scoped>
.donation-section { background: var(--vh-gris-piedra); position: relative; padding: 7rem 5%; }
.donation-section::before {
  content: ''; position: absolute; top: 0; left: 0; width: 100%; height: 100%;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23FBA636' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}
.donation-box {
  max-width: 850px; margin: 0 auto; text-align: center;
  position: relative; z-index: 1; background: white;
  padding: 4rem; border-radius: 40px;
  box-shadow: 0 25px 80px var(--vh-sombra);
}
.donation-box h2 { font-size: 2.8rem; color: var(--vh-azul-profundo); margin-bottom: 1rem; }
.donation-box > p { font-size: 1.15rem; color: #555; margin-bottom: 2.5rem; line-height: 1.8; }
.amount-grid { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; margin-bottom: 2.5rem; }
.amount-btn {
  padding: 1rem 2rem; border: 2.5px solid var(--vh-azul-profundo);
  background: transparent; color: var(--vh-azul-profundo);
  border-radius: 16px; font-family: 'League Spartan', sans-serif;
  font-weight: 700; cursor: pointer; transition: var(--vh-transicion);
  font-size: 1.1rem; position: relative; overflow: hidden;
}
.amount-btn::before {
  content: ''; position: absolute; top: 0; left: 0; width: 100%; height: 100%;
  background: var(--vh-azul-profundo); transform: scaleX(0);
  transform-origin: right; transition: transform 0.4s ease; z-index: -1;
}
.amount-btn:hover::before, .amount-btn.active::before { transform: scaleX(1); transform-origin: left; }
.amount-btn:hover, .amount-btn.active { color: white; border-color: var(--vh-azul-profundo); transform: scale(1.05); }
.custom-input { margin-bottom: 2rem; text-align: left; }
.custom-input label { display: block; color: var(--vh-azul-profundo); font-weight: 700; margin-bottom: 0.5rem; font-size: 0.9rem; }
.custom-input input { width: 100%; padding: 1rem; border: 2px solid #ddd; border-radius: 12px; font-size: 1rem; transition: var(--vh-transicion); font-family: 'Open Sans', sans-serif; }
.custom-input input:focus { outline: none; border-color: var(--vh-dorado); box-shadow: 0 0 0 4px rgba(251, 166, 54, 0.1); }
.method-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.2rem; margin-top: 2.5rem; }
.method-card { padding: 1.8rem 1rem; background: var(--vh-gris-piedra); border-radius: 20px; transition: var(--vh-transicion); cursor: pointer; border: 2px solid transparent; text-align: center; }
.method-card:hover, .method-card.active { background: var(--vh-azul-profundo); color: white; transform: translateY(-5px); box-shadow: 0 10px 30px rgba(26, 75, 140, 0.3); border-color: var(--vh-dorado); }
.method-card i { font-size: 2.2rem; color: var(--vh-dorado); margin-bottom: 0.8rem; transition: var(--vh-transicion); display: block; }
.method-card:hover i, .method-card.active i { color: var(--vh-dorado); transform: scale(1.2); }
.method-card span { display: block; font-weight: 700; font-size: 0.9rem; }
.donate-action {
  --background: var(--vh-verde);
  --color: white;
  --border-radius: 50px;
  font-size: 1.1rem; font-weight: 700;
  margin-top: 1rem;
}
.donate-action:hover { --background: #2aa87a; }
.donation-confirm { text-align: center; margin-top: 2rem; padding: 1.5rem; background: rgba(61, 194, 146, 0.1); border-radius: 16px; color: var(--vh-verde); font-weight: 700; display: none; animation: fadeInUp 0.5s ease; }
.donation-confirm.show { display: block; }

@media (max-width: 768px) {
  .donation-box { padding: 2.5rem; border-radius: 24px; }
  .donation-box h2 { font-size: 2rem; }
  .method-grid { grid-template-columns: 1fr; }
}
</style>
